<form
  (ngSubmit)="handleSave()"
  [formGroup]="accountBillingDeatilForm"
  class="billing-form"
>
  <!-- ===================================================== -->
  <!-- PAYMENT METHOD -->
  <!-- ===================================================== -->

  <section class="settings-card">
    <div class="section-copy">
      <span class="section-kicker">Payment Method</span>
      <h3>Card Details</h3>
      <p>
        Manage your payment method here. Card details are handled securely by
        Square.
      </p>
    </div>

    <div class="field-grid" formGroupName="cardDetails">
      <label class="field">
        <span>Name on card *</span>

        <input
          type="text"
          formControlName="name"
          placeholder="Olivia Rhye"
          [class.is-invalid]="isInvalid('cardDetails.name')"
        />

        <small *ngIf="isInvalid('cardDetails.name')">
          Name on card is required.
        </small>
      </label>

      <div *ngIf="hasExistingCard && !showCardEntry" class="saved-card">
        <div>
          <strong>Saved card</strong>
          <span>{{ existingCardSummary || "Card on file" }}</span>
        </div>

        <button type="button" class="light-btn" (click)="onReplaceCard()">
          Replace Card
        </button>
      </div>

      <div *ngIf="showCardEntry" class="field">
        <span>Card information *</span>

        <div
          id="square-card-container"
          #squareCardContainer
          class="square-card-box"
        ></div>

        <small class="hint">
          Square securely collects card number, expiry, postal code, and CVV.
        </small>

        <small *ngIf="squareError" class="error-text">
          {{ squareError }}
        </small>
      </div>
    </div>
  </section>

  <!-- ===================================================== -->
  <!-- RECURRING BILLING AUTHORIZATION -->
  <!-- ===================================================== -->

  <section class="settings-card">
    <div class="section-copy">
      <span class="section-kicker">Automatic Billing</span>
      <h3>Recurring Payment Authorization</h3>

      <p>
        Authorize gloget to charge your saved card for monthly appointment fees.
      </p>
    </div>

    <div class="authorization-content">
      <!-- Existing authorization -->

      <div
        *ngIf="paymentAuthorized"
        class="authorization-status authorization-status--active"
      >
        <div>
          <strong>Automatic billing authorized</strong>

          <p>
            Your saved card is authorized for recurring monthly gloget charges.
          </p>

          <small *ngIf="authorizationAcceptedAt">
            Authorized on
            {{ formatDateTime(authorizationAcceptedAt) }}
          </small>
        </div>

        <button
          type="button"
          class="light-btn"
          (click)="revokeRecurringBilling()"
          [disabled]="isRevokingAuthorization || isSubmitting"
        >
          {{ isRevokingAuthorization ? "Revoking..." : "Revoke Authorization" }}
        </button>
      </div>

      <!-- No authorization -->

      <div
        *ngIf="!paymentAuthorized"
        class="authorization-status authorization-status--inactive"
      >
        <strong>Automatic billing not authorized</strong>

        <p>
          gloget does not currently have an active recurring payment
          authorization for your saved card.
        </p>

        <p *ngIf="hasExistingCard && !showCardEntry">
          To authorize automatic billing, select Replace Card above and complete
          the authorization.
        </p>
      </div>

      <!-- Agreement -->

      <div *ngIf="showCardEntry" class="billing-agreement">
        <h4>Recurring Billing Agreement</h4>

        <div class="agreement-text">
          {{ agreementText || "Loading billing agreement..." }}
        </div>

        <label class="agreement-checkbox">
          <input
            type="checkbox"
            name="acceptRecurringBilling"
            [(ngModel)]="acceptRecurringBilling"
            [ngModelOptions]="{ standalone: true }"
            [disabled]="!agreementText || isSubmitting"
          />

          <span>
            I have read and agree to the recurring billing authorization above.
            I authorize gloget to charge my saved payment card for variable
            monthly appointment fees and applicable taxes.
          </span>
        </label>

        <small class="agreement-note">
          You can revoke this authorization in your billing settings. Revocation
          does not cancel outstanding invoices.
        </small>
      </div>
    </div>
  </section>

  <!-- ===================================================== -->
  <!-- BILLING EMAIL -->
  <!-- ===================================================== -->

  <section class="settings-card">
    <div class="section-copy">
      <span class="section-kicker">Invoices</span>
      <h3>Billing Email</h3>
      <p>Invoices and billing notices will be sent to this email address.</p>
    </div>

    <div class="field-grid">
      <label class="field">
        <span>Email address *</span>

        <input
          type="email"
          formControlName="email"
          placeholder="billing@gloget.com"
          [class.is-invalid]="isInvalid('email')"
        />

        <small *ngIf="isInvalid('email')">
          A valid billing email is required.
        </small>
      </label>
    </div>
  </section>

  <!-- ===================================================== -->
  <!-- BILLING ADDRESS -->
  <!-- ===================================================== -->

  <section class="settings-card" formGroupName="address">
    <div class="section-copy">
      <span class="section-kicker">Billing Address</span>
      <h3>Address Details</h3>
      <p>Used for invoices, receipts, and payment verification.</p>
    </div>

    <div class="field-grid">
      <label class="field">
        <span>Street address *</span>

        <input
          type="text"
          formControlName="address"
          placeholder="100 Smith Street"
          [class.is-invalid]="isInvalid('address.address')"
        />

        <small *ngIf="isInvalid('address.address')">
          Street address is required.
        </small>
      </label>

      <label class="field">
        <span>City *</span>

        <input
          type="text"
          formControlName="city"
          placeholder="Toronto"
          [class.is-invalid]="isInvalid('address.city')"
        />

        <small *ngIf="isInvalid('address.city')"> City is required. </small>
      </label>

      <div class="two-col">
        <label class="field">
          <span>State / Province *</span>

          <input
            type="text"
            formControlName="state"
            placeholder="ON"
            [class.is-invalid]="isInvalid('address.state')"
          />

          <small *ngIf="isInvalid('address.state')">
            State / Province is required.
          </small>
        </label>

        <label class="field">
          <span>ZIP / Postal code *</span>

          <input
            type="text"
            formControlName="zip"
            placeholder="M5V 2T6"
            [class.is-invalid]="isInvalid('address.zip')"
          />

          <small *ngIf="isInvalid('address.zip')">
            ZIP / postal code is required.
          </small>
        </label>
      </div>

      <label class="field">
        <span>Country *</span>

        <select
          formControlName="country"
          [class.is-invalid]="isInvalid('address.country')"
        >
          <option value="">Select country</option>

          <option *ngFor="let country of countries" [value]="country">
            {{ country }}
          </option>
        </select>

        <small *ngIf="isInvalid('address.country')">
          Country is required.
        </small>
      </label>
    </div>
  </section>

  <!-- ===================================================== -->
  <!-- BILLING HISTORY -->
  <!-- ===================================================== -->

  <section class="settings-card">
    <div class="section-copy">
      <span class="section-kicker">History</span>
      <h3>Billing History</h3>

      <p>
        Review monthly appointment charges and download invoice PDFs. Please
        reach out to
        <a href="mailto:billing@gloget.com" class="underline">
          billing&#64;gloget.com
        </a>
        with questions.
      </p>
    </div>

    <div class="billing-history">
      <div class="billing-head">
        <span>Invoice</span>
        <span>Appointments</span>
        <span>Status</span>
        <span>Amount</span>
        <span></span>
      </div>

      <div *ngIf="!billingMonths.length && !isLoading" class="empty-history">
        No billing history yet.
      </div>

      <div *ngIf="isLoading" class="empty-history">
        Loading billing history...
      </div>

      <div *ngFor="let invoice of billingMonths" class="billing-row">
        <div class="invoice-name">
          <strong>{{ invoice.name || "Invoice" }}</strong>

          <span *ngIf="invoice.period_start && invoice.period_end">
            {{ formatBillingPeriod(invoice) }}
          </span>
        </div>

        <span class="appointment-count">
          {{ invoice.appointment_count || 0 }}
        </span>

        <span>
          <span
            class="status-pill"
            [class.is-paid]="isPaid(invoice)"
            [class.is-open]="isOpen(invoice)"
            [class.is-failed]="isFailed(invoice)"
            [class.is-void]="isVoid(invoice)"
          >
            <img
              *ngIf="isPaid(invoice)"
              [src]="assetUrl + 'icons/check-green.svg'"
              class="status-icon"
              alt=""
            />

            {{ invoice.status || "Pending" }}
          </span>
        </span>

        <strong class="invoice-amount">
          {{ formatMoney(invoice.amount) }}
        </strong>

        <div class="invoice-actions">
          <button
            type="button"
            class="table-action-btn"
            (click)="viewInvoice(invoice)"
          >
            View
          </button>

          <button
            type="button"
            class="table-action-btn table-action-btn--primary"
            (click)="downloadInvoicePdf(invoice)"
            [disabled]="downloadingInvoiceId === invoice.id"
          >
            {{ downloadingInvoiceId === invoice.id ? "Downloading..." : "PDF" }}
          </button>
        </div>
      </div>
    </div>
  </section>

  <!-- ===================================================== -->
  <!-- FORM ACTIONS -->
  <!-- ===================================================== -->

  <div class="form-footer">
    <button
      type="button"
      class="light-btn"
      (click)="onCancel()"
      [disabled]="isSubmitting || isLoading"
    >
      Cancel
    </button>

    <button
      type="submit"
      class="dark-btn"
      [disabled]="isSubmitting || isLoading"
    >
      {{ isSubmitting ? "Saving..." : "Save" }}
    </button>
  </div>
</form>

<!-- ======================================================= -->
<!-- INVOICE MODAL -->
<!-- ======================================================= -->

<div
  *ngIf="showInvoiceModal"
  class="invoice-modal-backdrop"
  (click)="closeInvoiceModal()"
>
  <div
    class="invoice-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="invoice-modal-title"
    (click)="$event.stopPropagation()"
  >
    <div class="invoice-modal-header">
      <div>
        <span class="section-kicker">Invoice</span>

        <h2 id="invoice-modal-title">
          {{ selectedInvoice?.name || "Invoice Details" }}
        </h2>

        <p *ngIf="selectedInvoice">
          {{ formatBillingPeriod(selectedInvoice) }}
        </p>
      </div>

      <button
        type="button"
        class="modal-close"
        aria-label="Close invoice"
        (click)="closeInvoiceModal()"
      >
        ×
      </button>
    </div>

    <div *ngIf="invoiceDetailLoading" class="invoice-modal-loading">
      Loading invoice...
    </div>

    <ng-container *ngIf="!invoiceDetailLoading && selectedInvoice">
      <!-- Summary -->

      <div class="invoice-summary-grid">
        <div class="invoice-summary-card">
          <span>Appointments</span>
          <strong>{{ selectedInvoice.appointment_count || 0 }}</strong>
        </div>

        <div class="invoice-summary-card">
          <span>Rate</span>
          <strong>
            {{ formatMoney(selectedInvoice.price_per_appointment) }}
          </strong>
        </div>

        <div class="invoice-summary-card">
          <span>Status</span>

          <strong>
            <span
              class="status-pill"
              [class.is-paid]="isPaid(selectedInvoice)"
              [class.is-open]="isOpen(selectedInvoice)"
              [class.is-failed]="isFailed(selectedInvoice)"
              [class.is-void]="isVoid(selectedInvoice)"
            >
              {{ selectedInvoice.status || "Pending" }}
            </span>
          </strong>
        </div>

        <div class="invoice-summary-card">
          <span>Total</span>
          <strong>{{ formatMoney(selectedInvoice.amount) }}</strong>
        </div>
      </div>

      <!-- Appointment charges -->

      <div class="invoice-section">
        <div class="invoice-section-heading">
          <div>
            <h3>Appointment Charges</h3>
            <p>Appointments included in this monthly billing period.</p>
          </div>

          <span class="item-count">
            {{ selectedInvoice.items?.length || 0 }}
          </span>
        </div>

        <div *ngIf="!selectedInvoice.items?.length" class="invoice-empty-items">
          No appointment charges are attached to this invoice.
        </div>

        <div *ngIf="selectedInvoice.items?.length" class="invoice-items-table">
          <div class="invoice-items-head">
            <span>Salon</span>
            <span>Booked</span>
            <span>Appointment</span>
            <span>Status</span>
            <span>Charge</span>
          </div>

          <div
            *ngFor="let item of selectedInvoice.items"
            class="invoice-item-row"
          >
            <div class="invoice-item-main">
              <strong>{{ item.salon_name || "Salon" }}</strong>

              <span>
                {{ shortId(item.appointment_id) }}
              </span>
            </div>

            <span>
              {{ formatDateTime(item.appointment_created_at) }}
            </span>

            <span>
              {{ formatDateTime(item.appointment_start_time) }}
            </span>

            <span>
              <span class="mini-status">
                {{ formatStatus(item.appointment_status) }}
              </span>
            </span>

            <strong>
              {{ formatMoney(item.amount) }}
            </strong>
          </div>
        </div>
      </div>

      <!-- Totals -->

      <div class="invoice-totals">
        <div class="invoice-total-row">
          <span>
            {{ selectedInvoice.appointment_count || 0 }}
            appointment{{ selectedInvoice.appointment_count === 1 ? "" : "s" }}
            ×
            {{ formatMoney(selectedInvoice.price_per_appointment) }}
          </span>

          <strong>
            {{ formatMoney(selectedInvoice.subtotal) }}
          </strong>
        </div>

        <div class="invoice-total-row">
          <span>Tax</span>
          <strong>{{ formatMoney(selectedInvoice.tax_amount) }}</strong>
        </div>

        <div class="invoice-total-row invoice-total-row--grand">
          <span>Total</span>
          <strong>{{ formatMoney(selectedInvoice.amount) }}</strong>
        </div>
      </div>

      <!-- Invoice metadata -->

      <div class="invoice-meta">
        <div>
          <span>Invoice ID</span>
          <strong>{{ selectedInvoice.id }}</strong>
        </div>

        <div>
          <span>Plan</span>
          <strong>{{ selectedInvoice.plan || "Pay per appointment" }}</strong>
        </div>

        <div *ngIf="selectedInvoice.issued_at">
          <span>Issued</span>
          <strong>{{ formatDateTime(selectedInvoice.issued_at) }}</strong>
        </div>

        <div *ngIf="selectedInvoice.paid_at">
          <span>Paid</span>
          <strong>{{ formatDateTime(selectedInvoice.paid_at) }}</strong>
        </div>
      </div>
    </ng-container>

    <div class="invoice-modal-footer">
      <button type="button" class="light-btn" (click)="closeInvoiceModal()">
        Close
      </button>

      <button
        *ngIf="selectedInvoice"
        type="button"
        class="dark-btn"
        (click)="downloadInvoicePdf(selectedInvoice)"
        [disabled]="downloadingInvoiceId === selectedInvoice.id"
      >
        {{
          downloadingInvoiceId === selectedInvoice.id
            ? "Downloading..."
            : "Download PDF"
        }}
      </button>
    </div>
  </div>
</div>
